import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  BillingJobFactsPatch,
  BillingLine,
  BillingRun,
  Client,
  InspectionJob,
  RateCard,
  TravelClaim,
  TravelClaimPatch,
  UninvoicedJob,
  UpsertRateCard,
} from '@cer-autofill/shared';
import { WORKBOOK_RATE_CARD_TEMPLATES } from '@cer-autofill/shared';
import {
  acknowledgeBillingWarning,
  approveBillingRun,
  downloadBillingRunExport,
  downloadRateCardDocument,
  createBillingRun,
  fetchBillingRuns,
  fetchClients,
  fetchRateCards,
  fetchUninvoiced,
  fetchCase,
  issueBillingRun,
  refreshBillingRun,
  saveRateCard,
  updateBillingLine,
  updateBillingJobFacts,
  updateTravelClaim,
  uploadRateCardDocument,
} from '../api.js';
import { Kpi } from '../components/Kpi.js';
import { percentage } from '../components/trend.js';

type Tab = 'runs' | 'rates' | 'ledger';
const money = (value: number | null) =>
  value === null
    ? '—'
    : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
const numberValue = (value: string) => Number(value || 0);

function emptyCard(clientId = ''): UpsertRateCard {
  return {
    rateCardId: '',
    clientId,
    version: 1,
    effectiveFrom: '2026-07-01',
    effectiveTo: null,
    status: 'draft',
    inspectionRates: [
      { inspectionType: 'pv', kwMin: 0, kwMax: 10, price: 0 },
      { inspectionType: 'pv', kwMin: 10.01, kwMax: null, price: 0 },
      { inspectionType: 'bty', kwMin: 0, kwMax: 10, price: 0 },
      { inspectionType: 'bty', kwMin: 10.01, kwMax: null, price: 0 },
      { inspectionType: 'both', kwMin: 0, kwMax: 10, price: 0 },
      { inspectionType: 'both', kwMin: 10.01, kwMax: null, price: 0 },
    ],
    travel: {
      claimMethod: 'per-km',
      claimScope: 'all',
      routeBasis: 'supplier-day-round-trip',
      ratePerKm: 0,
      freeKmThreshold: 0,
      setRate: null,
      fuelLevyPerKm: 0,
      evidenceRequired: false,
      notes: '',
      defaultBase: {
        label: 'Geebung warehouse',
        address: 'Unit 9/94 Delta Street, Geebung QLD 4034',
      },
      baseOverrides: [],
    },
    accommodation: {
      claimMethod: 'fixed-nightly',
      perNight: 0,
      regionalVariants: [],
      perPerson: false,
      evidenceRequired: false,
      notes: '',
    },
    meals: {
      claimable: false,
      breakfast: 0,
      lunch: 0,
      dinner: 0,
      fullDay: 0,
      evidenceRequired: false,
      notes: '',
    },
    extras: [],
    milestoneComponents: [],
    gstTreatment: 'exGst',
    sourceDocument: null,
    notes: '',
  };
}

export function InvoicingScreen() {
  const [tab, setTab] = useState<Tab>('runs');
  const [clients, setClients] = useState<Client[]>([]);
  const [cards, setCards] = useState<RateCard[]>([]);
  const [runs, setRuns] = useState<BillingRun[]>([]);
  const [ledger, setLedger] = useState<UninvoicedJob[]>([]);
  const [selectedRun, setSelectedRun] = useState<BillingRun | null>(null);
  const [clientId, setClientId] = useState('');
  const [month, setMonth] = useState('2026-07');
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    const [nextClients, nextCards, nextRuns, nextLedger] = await Promise.all([
      fetchClients(true),
      fetchRateCards(),
      fetchBillingRuns(),
      fetchUninvoiced(),
    ]);
    setClients(nextClients);
    setCards(nextCards);
    setRuns(nextRuns);
    setLedger(nextLedger);
    setClientId((value) => value || nextClients[0]?.clientId || '');
    setSelectedRun((current) =>
      current
        ? (nextRuns.find((run) => run.billingRunId === current.billingRunId) ?? current)
        : null,
    );
  }
  useEffect(() => {
    void refresh().catch((cause) => setError(String(cause)));
  }, []);
  const ledgerTotal = useMemo(
    () => ledger.reduce((sum, row) => sum + (row.estimatedValue ?? 0), 0),
    [ledger],
  );

  return (
    <main className="screen invoicing">
      <div className="screen__header">
        <div>
          <h2>Invoicing</h2>
          <p className="subtitle">
            Price completed Jobs, clear QC, and prepare the monthly ServiceM8 billing shell.
          </p>
        </div>
      </div>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'runs'} onClick={() => setTab('runs')}>
          Billing runs
        </button>
        <button role="tab" aria-selected={tab === 'rates'} onClick={() => setTab('rates')}>
          Rate cards
        </button>
        <button role="tab" aria-selected={tab === 'ledger'} onClick={() => setTab('ledger')}>
          Uninvoiced <span className="count-badge">{ledger.length}</span>
        </button>
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {tab === 'rates' && (
        <RateCards clients={clients} cards={cards} onChanged={refresh} onError={setError} />
      )}
      {tab === 'runs' && (
        <BillingRuns
          clients={clients}
          cards={cards}
          runs={runs}
          selected={selectedRun}
          clientId={clientId}
          month={month}
          setClientId={setClientId}
          setMonth={setMonth}
          onSelect={setSelectedRun}
          onChanged={async (run) => {
            await refresh();
            setSelectedRun(run);
          }}
          onError={setError}
        />
      )}
      {tab === 'ledger' && <Ledger rows={ledger} total={ledgerTotal} clients={clients} />}
    </main>
  );
}

function RateCards({
  clients,
  cards,
  onChanged,
  onError,
}: {
  clients: Client[];
  cards: RateCard[];
  onChanged: () => Promise<void>;
  onError: (value: string | null) => void;
}) {
  const [form, setForm] = useState<UpsertRateCard>(() => emptyCard());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const currentCard = cards.find((card) => card.rateCardId === form.rateCardId);
  const workbookTemplate = WORKBOOK_RATE_CARD_TEMPLATES[form.clientId];
  const edit = (card: RateCard) => {
    const { createdAt: _c, updatedAt: _u, ...value } = card;
    setForm(value);
    setSaved(false);
  };
  const loadWorkbookTemplate = () => {
    if (!workbookTemplate) return;
    const nextVersion =
      Math.max(
        0,
        ...cards.filter((card) => card.clientId === form.clientId).map((card) => card.version),
      ) + 1;
    setForm({
      ...emptyCard(form.clientId),
      ...workbookTemplate,
      version: nextVersion,
      effectiveFrom: new Date().toISOString().slice(0, 10),
      status: 'draft',
    });
    setSaved(false);
  };
  async function submit() {
    setSaving(true);
    setSaved(false);
    try {
      const payload: UpsertRateCard = {
        ...form,
        rateCardId: form.rateCardId || `${form.clientId}-v${form.version}`,
      };
      const result = await saveRateCard(payload);
      await onChanged();
      edit(result);
      setSaved(true);
      onError(null);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <section className="invoice-section">
      <div className="section-heading">
        <div>
          <h3>Rate cards</h3>
          <p>
            Versioned commercial rules for inspection fees, travel, accommodation, meals, and
            evidence.
          </p>
        </div>
        <div className="rate-heading-actions">
          {workbookTemplate && (
            <button type="button" onClick={loadWorkbookTemplate}>
              Create workbook draft
            </button>
          )}
          <button
            onClick={() => {
              setForm(emptyCard(clients[0]?.clientId));
              setSaved(false);
            }}
          >
            New version
          </button>
        </div>
      </div>
      <div className="rate-layout">
        <div className="rate-list">
          {cards.map((card) => (
            <button
              key={card.rateCardId}
              className={form.rateCardId === card.rateCardId ? 'selected' : ''}
              onClick={() => edit(card)}
            >
              <strong>
                {clients.find((c) => c.clientId === card.clientId)?.shortName ?? card.clientId} · v
                {card.version}
              </strong>
              <span>
                {card.effectiveFrom} · {card.status}
              </span>
            </button>
          ))}
        </div>
        <div className="rate-editor">
          <div className="form-grid">
            <label>
              Client
              <select
                value={form.clientId}
                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
              >
                <option value="">Select…</option>
                {clients.map((client) => (
                  <option key={client.clientId} value={client.clientId}>
                    {client.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Version
              <input
                type="number"
                min="1"
                value={form.version}
                onChange={(e) => setForm({ ...form, version: Number(e.target.value) })}
              />
            </label>
            <label>
              Effective from
              <input
                type="date"
                value={form.effectiveFrom}
                onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
              />
            </label>
            <label>
              Effective to
              <input
                type="date"
                value={form.effectiveTo ?? ''}
                onChange={(e) => setForm({ ...form, effectiveTo: e.target.value || null })}
              />
            </label>
            <label>
              Status
              <select
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value as UpsertRateCard['status'] })
                }
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="superseded">Superseded</option>
              </select>
            </label>
            <label>
              GST
              <select
                value={form.gstTreatment}
                onChange={(e) =>
                  setForm({
                    ...form,
                    gstTreatment: e.target.value as UpsertRateCard['gstTreatment'],
                  })
                }
              >
                <option value="exGst">Ex GST</option>
                <option value="incGst">Inc GST</option>
              </select>
            </label>
          </div>
          <EditableTable
            title="Inspection rates"
            columns={['Type', 'From kW', 'To kW', 'Price', '']}
            onAdd={() =>
              setForm({
                ...form,
                inspectionRates: [
                  ...form.inspectionRates,
                  { inspectionType: 'pv', kwMin: 0, kwMax: null, price: 0 },
                ],
              })
            }
          >
            {form.inspectionRates.map((row, index) => (
              <div className="editable-grid editable-grid--rates" key={index}>
                <select
                  aria-label={`Rate ${index + 1} type`}
                  value={row.inspectionType}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      inspectionRates: form.inspectionRates.map((item, i) =>
                        i === index
                          ? { ...item, inspectionType: e.target.value as typeof row.inspectionType }
                          : item,
                      ),
                    })
                  }
                >
                  <option value="pv">PV</option>
                  <option value="bty">Battery</option>
                  <option value="both">PV + Battery</option>
                </select>
                <input
                  aria-label={`Rate ${index + 1} minimum kW`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={row.kwMin}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      inspectionRates: form.inspectionRates.map((item, i) =>
                        i === index ? { ...item, kwMin: numberValue(e.target.value) } : item,
                      ),
                    })
                  }
                />
                <input
                  aria-label={`Rate ${index + 1} maximum kW`}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="No maximum"
                  value={row.kwMax ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      inspectionRates: form.inspectionRates.map((item, i) =>
                        i === index
                          ? { ...item, kwMax: e.target.value ? Number(e.target.value) : null }
                          : item,
                      ),
                    })
                  }
                />
                <input
                  aria-label={`Rate ${index + 1} price`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={row.price}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      inspectionRates: form.inspectionRates.map((item, i) =>
                        i === index ? { ...item, price: numberValue(e.target.value) } : item,
                      ),
                    })
                  }
                />
                <button
                  type="button"
                  aria-label={`Remove rate ${index + 1}`}
                  onClick={() =>
                    setForm({
                      ...form,
                      inspectionRates: form.inspectionRates.filter((_, i) => i !== index),
                    })
                  }
                >
                  Remove
                </button>
              </div>
            ))}
          </EditableTable>
          <section className="rate-rule-card">
            <div className="rate-rule-card__heading">
              <div>
                <h4>Travel claim</h4>
                <p>Calculated independently for this supplier’s jobs on the inspection date.</p>
              </div>
              <span className="rule-badge">{form.travel.claimMethod}</span>
            </div>
            <div className="form-grid compact rate-rule-grid">
              <label>
                Claim method
                <select
                  value={form.travel.claimMethod}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      travel: {
                        ...form.travel,
                        claimMethod: e.target.value as UpsertRateCard['travel']['claimMethod'],
                      },
                    })
                  }
                >
                  <option value="per-km">Per kilometre</option>
                  <option value="fixed">Fixed amount</option>
                  <option value="quoted">Quoted / manual</option>
                  <option value="not-claimable">Not claimable</option>
                </select>
              </label>
              <label>
                Applies to
                <select
                  value={form.travel.claimScope}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      travel: {
                        ...form.travel,
                        claimScope: e.target.value as UpsertRateCard['travel']['claimScope'],
                      },
                    })
                  }
                >
                  <option value="all">All jobs</option>
                  <option value="regional-only">Regional only</option>
                  <option value="conditional">Conditional</option>
                </select>
              </label>
              <label>
                Route basis
                <select
                  value={form.travel.routeBasis}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      travel: {
                        ...form.travel,
                        routeBasis: e.target.value as UpsertRateCard['travel']['routeBasis'],
                      },
                    })
                  }
                >
                  <option value="supplier-day-round-trip">Base → supplier jobs → base</option>
                  <option value="manual">Manual route</option>
                </select>
              </label>
              <label>
                Travel $/km
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.travel.ratePerKm}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      travel: { ...form.travel, ratePerKm: numberValue(e.target.value) },
                    })
                  }
                />
              </label>
              <label>
                Free km threshold
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.travel.freeKmThreshold}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      travel: { ...form.travel, freeKmThreshold: numberValue(e.target.value) },
                    })
                  }
                />
              </label>
              <label>
                Fixed travel amount
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.travel.setRate ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      travel: {
                        ...form.travel,
                        setRate: e.target.value ? Number(e.target.value) : null,
                      },
                    })
                  }
                />
              </label>
              <label>
                Fuel levy $/km
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.travel.fuelLevyPerKm}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      travel: { ...form.travel, fuelLevyPerKm: numberValue(e.target.value) },
                    })
                  }
                />
              </label>
              <label className="checkbox-label rate-checkbox">
                <input
                  type="checkbox"
                  checked={form.travel.evidenceRequired}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      travel: { ...form.travel, evidenceRequired: e.target.checked },
                    })
                  }
                />{' '}
                Route evidence required
              </label>
            </div>
            <div className="form-grid compact rate-rule-grid">
              <label>
                Default base label
                <input
                  value={form.travel.defaultBase.label}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      travel: {
                        ...form.travel,
                        defaultBase: { ...form.travel.defaultBase, label: e.target.value },
                      },
                    })
                  }
                />
              </label>
              <label>
                Default base address
                <input
                  value={form.travel.defaultBase.address}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      travel: {
                        ...form.travel,
                        defaultBase: { ...form.travel.defaultBase, address: e.target.value },
                      },
                    })
                  }
                />
              </label>
            </div>
            <EditableTable
              title="Inspector base exceptions"
              columns={['Inspector ID', 'Label', 'Address', 'Reason', '']}
              onAdd={() =>
                setForm({
                  ...form,
                  travel: {
                    ...form.travel,
                    baseOverrides: [
                      ...form.travel.baseOverrides,
                      { inspectorId: '', label: '', address: '', notes: '' },
                    ],
                  },
                })
              }
            >
              {form.travel.baseOverrides.map((row, index) => (
                <div
                  className="editable-grid editable-grid--extras"
                  key={`${row.inspectorId}-${index}`}
                >
                  {(['inspectorId', 'label', 'address', 'notes'] as const).map((key) => (
                    <input
                      key={key}
                      aria-label={`Base exception ${index + 1} ${key}`}
                      value={row[key]}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          travel: {
                            ...form.travel,
                            baseOverrides: form.travel.baseOverrides.map((item, i) =>
                              i === index ? { ...item, [key]: e.target.value } : item,
                            ),
                          },
                        })
                      }
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        travel: {
                          ...form.travel,
                          baseOverrides: form.travel.baseOverrides.filter((_, i) => i !== index),
                        },
                      })
                    }
                  >
                    Remove
                  </button>
                </div>
              ))}
            </EditableTable>
            <label>
              Travel conditions and evidence notes
              <textarea
                rows={2}
                value={form.travel.notes}
                onChange={(e) =>
                  setForm({ ...form, travel: { ...form.travel, notes: e.target.value } })
                }
              />
            </label>
          </section>

          <section className="rate-rule-card">
            <div className="rate-rule-card__heading">
              <div>
                <h4>Accommodation</h4>
                <p>Capture fixed allowances separately from actual-cost reimbursement.</p>
              </div>
              <span className="rule-badge">{form.accommodation.claimMethod}</span>
            </div>
            <div className="form-grid compact rate-rule-grid">
              <label>
                Claim method
                <select
                  value={form.accommodation.claimMethod}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      accommodation: {
                        ...form.accommodation,
                        claimMethod: e.target
                          .value as UpsertRateCard['accommodation']['claimMethod'],
                      },
                    })
                  }
                >
                  <option value="fixed-nightly">Fixed nightly rate</option>
                  <option value="actual-cost">Actual cost</option>
                  <option value="manual">Manual assessment</option>
                  <option value="not-claimable">Not claimable</option>
                </select>
              </label>
              <label>
                Accommodation/night
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.accommodation.perNight}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      accommodation: {
                        ...form.accommodation,
                        perNight: numberValue(e.target.value),
                      },
                    })
                  }
                />
              </label>
              <label className="checkbox-label rate-checkbox">
                <input
                  type="checkbox"
                  checked={form.accommodation.perPerson}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      accommodation: { ...form.accommodation, perPerson: e.target.checked },
                    })
                  }
                />{' '}
                Rate is per person
              </label>
              <label className="checkbox-label rate-checkbox">
                <input
                  type="checkbox"
                  checked={form.accommodation.evidenceRequired}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      accommodation: { ...form.accommodation, evidenceRequired: e.target.checked },
                    })
                  }
                />{' '}
                Receipt required
              </label>
            </div>
            <label>
              Accommodation conditions
              <textarea
                rows={2}
                value={form.accommodation.notes}
                onChange={(e) =>
                  setForm({
                    ...form,
                    accommodation: { ...form.accommodation, notes: e.target.value },
                  })
                }
              />
            </label>
          </section>

          <section className="rate-rule-card">
            <div className="rate-rule-card__heading">
              <div>
                <h4>Meals</h4>
                <p>Optional full-day or meal-period allowances.</p>
              </div>
              <span className="rule-badge">
                {form.meals.claimable ? 'claimable' : 'not claimable'}
              </span>
            </div>
            <div className="form-grid compact rate-rule-grid">
              <label className="checkbox-label rate-checkbox">
                <input
                  type="checkbox"
                  checked={form.meals.claimable}
                  onChange={(e) =>
                    setForm({ ...form, meals: { ...form.meals, claimable: e.target.checked } })
                  }
                />{' '}
                Meals claimable
              </label>
              <label>
                Breakfast
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.meals.breakfast}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      meals: { ...form.meals, breakfast: numberValue(e.target.value) },
                    })
                  }
                />
              </label>
              <label>
                Lunch
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.meals.lunch}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      meals: { ...form.meals, lunch: numberValue(e.target.value) },
                    })
                  }
                />
              </label>
              <label>
                Dinner
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.meals.dinner}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      meals: { ...form.meals, dinner: numberValue(e.target.value) },
                    })
                  }
                />
              </label>
              <label>
                Full day
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.meals.fullDay}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      meals: { ...form.meals, fullDay: numberValue(e.target.value) },
                    })
                  }
                />
              </label>
              <label className="checkbox-label rate-checkbox">
                <input
                  type="checkbox"
                  checked={form.meals.evidenceRequired}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      meals: { ...form.meals, evidenceRequired: e.target.checked },
                    })
                  }
                />{' '}
                Evidence required
              </label>
            </div>
            <label>
              Meal eligibility notes
              <textarea
                rows={2}
                value={form.meals.notes}
                onChange={(e) =>
                  setForm({ ...form, meals: { ...form.meals, notes: e.target.value } })
                }
              />
            </label>
          </section>
          <EditableTable
            title="Regional accommodation"
            columns={['Area', 'Per night', '']}
            onAdd={() =>
              setForm({
                ...form,
                accommodation: {
                  ...form.accommodation,
                  regionalVariants: [
                    ...form.accommodation.regionalVariants,
                    { area: '', perNight: 0 },
                  ],
                },
              })
            }
          >
            {form.accommodation.regionalVariants.map((row, index) => (
              <div className="editable-grid editable-grid--region" key={index}>
                <input
                  aria-label={`Region ${index + 1} area`}
                  value={row.area}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      accommodation: {
                        ...form.accommodation,
                        regionalVariants: form.accommodation.regionalVariants.map((item, i) =>
                          i === index ? { ...item, area: e.target.value } : item,
                        ),
                      },
                    })
                  }
                />
                <input
                  aria-label={`Region ${index + 1} price`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={row.perNight}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      accommodation: {
                        ...form.accommodation,
                        regionalVariants: form.accommodation.regionalVariants.map((item, i) =>
                          i === index ? { ...item, perNight: numberValue(e.target.value) } : item,
                        ),
                      },
                    })
                  }
                />
                <button
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      accommodation: {
                        ...form.accommodation,
                        regionalVariants: form.accommodation.regionalVariants.filter(
                          (_, i) => i !== index,
                        ),
                      },
                    })
                  }
                >
                  Remove
                </button>
              </div>
            ))}
          </EditableTable>
          <EditableTable
            title="Extras and allowances"
            columns={['Code', 'Description', 'Price', 'Unit', '']}
            onAdd={() =>
              setForm({
                ...form,
                extras: [...form.extras, { code: '', label: '', price: 0, unit: 'job' }],
              })
            }
          >
            {form.extras.map((row, index) => (
              <div className="editable-grid editable-grid--extras" key={index}>
                <input
                  aria-label={`Extra ${index + 1} code`}
                  value={row.code}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      extras: form.extras.map((item, i) =>
                        i === index ? { ...item, code: e.target.value } : item,
                      ),
                    })
                  }
                />
                <input
                  aria-label={`Extra ${index + 1} description`}
                  value={row.label}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      extras: form.extras.map((item, i) =>
                        i === index ? { ...item, label: e.target.value } : item,
                      ),
                    })
                  }
                />
                <input
                  aria-label={`Extra ${index + 1} price`}
                  type="number"
                  step="0.01"
                  value={row.price}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      extras: form.extras.map((item, i) =>
                        i === index ? { ...item, price: numberValue(e.target.value) } : item,
                      ),
                    })
                  }
                />
                <select
                  aria-label={`Extra ${index + 1} unit`}
                  value={row.unit}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      extras: form.extras.map((item, i) =>
                        i === index ? { ...item, unit: e.target.value as typeof row.unit } : item,
                      ),
                    })
                  }
                >
                  <option value="job">Per job</option>
                  <option value="each">Each</option>
                  <option value="night">Per night</option>
                  <option value="km">Per km</option>
                </select>
                <button
                  type="button"
                  onClick={() =>
                    setForm({ ...form, extras: form.extras.filter((_, i) => i !== index) })
                  }
                >
                  Remove
                </button>
              </div>
            ))}
          </EditableTable>
          <EditableTable
            title="Milestone components"
            columns={['Code', 'Description', 'Price', '']}
            onAdd={() =>
              setForm({
                ...form,
                milestoneComponents: [
                  ...form.milestoneComponents,
                  { code: '', label: '', price: 0 },
                ],
              })
            }
          >
            {form.milestoneComponents.map((row, index) => (
              <div className="editable-grid editable-grid--milestones" key={index}>
                <input
                  aria-label={`Milestone ${index + 1} code`}
                  value={row.code}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      milestoneComponents: form.milestoneComponents.map((item, i) =>
                        i === index ? { ...item, code: e.target.value } : item,
                      ),
                    })
                  }
                />
                <input
                  aria-label={`Milestone ${index + 1} description`}
                  value={row.label}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      milestoneComponents: form.milestoneComponents.map((item, i) =>
                        i === index ? { ...item, label: e.target.value } : item,
                      ),
                    })
                  }
                />
                <input
                  aria-label={`Milestone ${index + 1} price`}
                  type="number"
                  step="0.01"
                  value={row.price}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      milestoneComponents: form.milestoneComponents.map((item, i) =>
                        i === index ? { ...item, price: numberValue(e.target.value) } : item,
                      ),
                    })
                  }
                />
                <button
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      milestoneComponents: form.milestoneComponents.filter((_, i) => i !== index),
                    })
                  }
                >
                  Remove
                </button>
              </div>
            ))}
          </EditableTable>
          <label>
            Notes
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>
          <div className="form-actions">
            <button
              className="primary"
              disabled={saving || !form.clientId}
              onClick={() => void submit()}
            >
              {saving ? 'Saving…' : 'Save rate card'}
            </button>
            {saved && (
              <span className="save-confirmation" role="status">
                Saved
              </span>
            )}
            {form.rateCardId && (
              <label className="upload-button">
                {form.sourceDocument?.fileName ?? 'Upload source document'}
                <input
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file)
                      void uploadRateCardDocument(form.rateCardId, file)
                        .then(() => onChanged())
                        .catch((cause) => onError(String(cause)));
                  }}
                />
              </label>
            )}
            {currentCard?.sourceDocument && (
              <button
                onClick={() =>
                  void downloadRateCardDocument(currentCard).catch((cause) =>
                    onError(String(cause)),
                  )
                }
              >
                Download evidence
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function EditableTable({
  title,
  columns,
  onAdd,
  children,
}: {
  title: string;
  columns: string[];
  onAdd: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="editable-table">
      <div className="editable-table__title">
        <h4>{title}</h4>
        <button type="button" onClick={onAdd}>
          + Add row
        </button>
      </div>
      <div
        className="editable-table__head"
        style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
      >
        {columns.map((column, index) => (
          <span key={`${column}-${index}`}>{column}</span>
        ))}
      </div>
      {children}
      <div className="editable-table__empty">No rows</div>
    </section>
  );
}

function BillingRuns(props: {
  clients: Client[];
  cards: RateCard[];
  runs: BillingRun[];
  selected: BillingRun | null;
  clientId: string;
  month: string;
  setClientId: (value: string) => void;
  setMonth: (value: string) => void;
  onSelect: (run: BillingRun) => void;
  onChanged: (run: BillingRun) => Promise<void>;
  onError: (value: string | null) => void;
}) {
  const {
    clients,
    cards,
    runs,
    selected,
    clientId,
    month,
    setClientId,
    setMonth,
    onSelect,
    onChanged,
    onError,
  } = props;
  const visibleRuns = useMemo(
    () => runs.filter((run) => run.clientId === clientId),
    [clientId, runs],
  );
  useEffect(() => {
    if (selected?.clientId === clientId && selected.month === month) return;
    const candidate = visibleRuns.find((run) => run.month === month) ?? visibleRuns[0];
    if (candidate) onSelect(candidate);
  }, [clientId, month, onSelect, selected?.clientId, visibleRuns]);
  async function action(work: Promise<BillingRun>): Promise<boolean> {
    try {
      const run = await work;
      await onChanged(run);
      onError(null);
      return true;
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : String(cause));
      return false;
    }
  }
  return (
    <section className="invoice-section">
      <div className="run-create">
        <label>
          Client
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            {clients.map((client) => (
              <option key={client.clientId} value={client.clientId}>
                {client.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Month
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <button className="primary" onClick={() => void action(createBillingRun(clientId, month))}>
          Create / open run
        </button>
      </div>
      <div className="run-layout">
        <aside className="run-list" aria-label="Billing runs for selected Client">
          {visibleRuns.map((run) => (
            <button
              key={run.billingRunId}
              className={selected?.billingRunId === run.billingRunId ? 'selected' : ''}
              onClick={() => onSelect(run)}
            >
              <strong>
                {clients.find((c) => c.clientId === run.clientId)?.shortName ?? run.clientId} ·{' '}
                {run.month}
              </strong>
              <span>
                {run.lines.length} Jobs ·{' '}
                {money(run.lines.reduce((sum, line) => sum + line.total, 0))} · {run.status}
              </span>
            </button>
          ))}
          {visibleRuns.length === 0 && <p className="muted">No runs for this Client yet.</p>}
        </aside>
        {selected?.clientId === clientId ? (
          <RunDetail
            run={selected}
            clients={clients}
            cards={cards.filter((card) => card.clientId === selected.clientId)}
            action={action}
            onError={onError}
          />
        ) : (
          <div className="empty-state">Select or create a billing run.</div>
        )}
      </div>
    </section>
  );
}

function RunDetail({
  run,
  clients,
  cards,
  action,
  onError,
}: {
  run: BillingRun;
  clients: Client[];
  cards: RateCard[];
  action: (work: Promise<BillingRun>) => Promise<boolean>;
  onError: (value: string | null) => void;
}) {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [openCheck, setOpenCheck] = useState<string | null>(null);
  const [resolvingJob, setResolvingJob] = useState<InspectionJob | null>(null);
  const [editingLine, setEditingLine] = useState<BillingLine | null>(null);
  const [editingTravel, setEditingTravel] = useState<TravelClaim | null>(null);
  const [acknowledging, setAcknowledging] = useState<{ code: string; label: string } | null>(null);
  const travelTotal = run.travelClaims
    .filter((claim) => claim.status !== 'excluded')
    .reduce((sum, claim) => sum + claim.total, 0);
  const total = run.lines.reduce((sum, line) => sum + line.total, 0) + travelTotal;
  const selectedCheck = run.qcChecks.find((item) => item.code === openCheck);
  return (
    <div className="run-detail">
      <div className="section-heading">
        <div>
          <h3>
            {clients.find((c) => c.clientId === run.clientId)?.name} · {run.month}
          </h3>
          <p>
            {run.lines.length} Jobs · <strong>{money(total)}</strong> · {run.status}
          </p>
        </div>
        <div className="form-actions">
          {run.status === 'draft' && (
            <button onClick={() => void action(refreshBillingRun(run.billingRunId))}>
              Refresh pricing &amp; re-run QA
            </button>
          )}
          <button
            onClick={() =>
              void downloadBillingRunExport(run.billingRunId).catch((cause) =>
                onError(String(cause)),
              )
            }
          >
            Export breakdown
          </button>
        </div>
      </div>
      <div className="qc-grid">
        {run.qcChecks.map((item) => (
          <button
            type="button"
            key={item.code}
            className={`qc-card ${item.passed ? 'pass' : item.severity} ${openCheck === item.code ? 'selected' : ''}`}
            aria-expanded={openCheck === item.code}
            onClick={() => setOpenCheck((value) => (value === item.code ? null : item.code))}
          >
            <strong>{item.passed ? 'PASS' : item.severity.toUpperCase()}</strong>
            <span>{item.label}</span>
            <small>{item.details}</small>
            <em>View evidence →</em>
          </button>
        ))}
      </div>
      {selectedCheck && (
        <div
          className={`qc-evidence ${selectedCheck.passed ? 'pass' : selectedCheck.severity}`}
          role="region"
          aria-label={`${selectedCheck.label} evidence`}
        >
          <div className="section-heading">
            <div>
              <strong>{selectedCheck.label}</strong>
              <p>{selectedCheck.details}</p>
            </div>
            <button type="button" onClick={() => setOpenCheck(null)}>
              Close
            </button>
          </div>
          {selectedCheck.evidence.length ? (
            <div className="qc-evidence__list">
              {selectedCheck.evidence.map((item) => (
                <div className="qc-evidence__item" key={`${selectedCheck.code}-${item.jobId}`}>
                  <strong>{item.label}</strong>
                  <span>{item.detail}</span>
                  <div className="form-actions">
                    <Link to={`/jobs/${item.jobId}`}>Open Job</Link>
                    {run.status === 'draft' && (
                      <button
                        type="button"
                        onClick={() =>
                          void fetchCase(item.jobId)
                            .then(setResolvingJob)
                            .catch((cause) => onError(String(cause)))
                        }
                      >
                        Resolve billing facts
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="qc-evidence__clear">
              No exceptions found across {selectedCheck.inspectedCount.toLocaleString()} inspected
              Job{selectedCheck.inspectedCount === 1 ? '' : 's'}.
            </p>
          )}
          {!selectedCheck.passed &&
            selectedCheck.severity === 'warning' &&
            !run.warningAcknowledgements[selectedCheck.code] && (
              <button
                onClick={() =>
                  setAcknowledging({ code: selectedCheck.code, label: selectedCheck.label })
                }
              >
                Acknowledge with note
              </button>
            )}
          {run.warningAcknowledgements[selectedCheck.code] && (
            <em>Acknowledged: {run.warningAcknowledgements[selectedCheck.code]}</em>
          )}
        </div>
      )}
      <section className="travel-claims">
        <div className="section-heading">
          <div>
            <h3>Travel claims</h3>
            <p>
              Supplier-day routes calculated as if this supplier’s jobs were completed in isolation.
            </p>
          </div>
          <strong>{money(travelTotal)}</strong>
        </div>
        <div className="billing-lines">
          <div className="billing-line header">
            <span>Date / route</span>
            <span>Jobs</span>
            <span>Kilometres</span>
            <span>Allowances</span>
            <span>Total</span>
            <span />
          </div>
          {run.travelClaims.map((claim) => (
            <div className="billing-line" key={claim.travelClaimId}>
              <span>
                <strong>{claim.date}</strong>
                <small>
                  {claim.baseLabel} → {claim.stopAddresses.join(' → ')} → {claim.baseLabel}
                </small>
              </span>
              <span>{claim.jobIds.length}</span>
              <span>{claim.routeKm ?? 'Review'} km</span>
              <span>
                <small>
                  Travel {money(claim.travelAmount)} · Accom {money(claim.accommodationAmount)} ·
                  Meals {money(claim.mealAmount)}
                </small>
              </span>
              <span>
                <strong>{money(claim.total)}</strong>
                <small>{claim.status}</small>
              </span>
              <span>
                {run.status === 'draft' && (
                  <button type="button" onClick={() => setEditingTravel(claim)}>
                    Review
                  </button>
                )}
              </span>
            </div>
          ))}
          {!run.travelClaims.length && <p>No supplier-day travel claims in this run.</p>}
        </div>
      </section>
      <div className="billing-lines">
        <div className="billing-line header">
          <span>Invoice line</span>
          <span>Fee</span>
          <span>Travel</span>
          <span>Extras</span>
          <span>Total</span>
          <span />
        </div>
        {run.lines.map((line) => (
          <BillingLineRow key={line.jobId} run={run} line={line} onEdit={setEditingLine} />
        ))}
      </div>
      <details className="shell-preview">
        <summary>ServiceM8 billing shell</summary>
        <div className="integration-notice">
          <strong>Automatic ServiceM8 creation is not enabled yet.</strong>
          <span>
            Approving this run locks the reviewed figures. Continue raising the invoice in ServiceM8
            using the exported breakdown.
          </span>
        </div>
      </details>
      <div className="run-actions">
        {run.status === 'draft' && (
          <button
            className="primary"
            onClick={() => void action(approveBillingRun(run.billingRunId))}
          >
            Approve run
          </button>
        )}
        {run.status === 'approved' && (
          <>
            <input
              placeholder="Invoice number"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />
            <button
              className="primary"
              onClick={() =>
                void action(issueBillingRun(run.billingRunId, invoiceNumber, invoiceDate))
              }
            >
              Record issued invoice
            </button>
          </>
        )}
      </div>
      {resolvingJob && (
        <BillingFactsModal
          job={resolvingJob}
          availableExtras={[
            ...new Map(
              cards
                .flatMap((card) => [...card.extras, ...card.milestoneComponents])
                .map((extra) => [extra.code, extra]),
            ).values(),
          ]}
          onCancel={() => setResolvingJob(null)}
          onSave={async (facts) => {
            const ok = await action(
              updateBillingJobFacts(run.billingRunId, resolvingJob.jobId, facts),
            );
            if (ok) setResolvingJob(null);
            return ok;
          }}
        />
      )}
      {editingLine && (
        <BillingLineModal
          line={editingLine}
          onCancel={() => setEditingLine(null)}
          onSave={async (line) => {
            const ok = await action(updateBillingLine(run.billingRunId, line));
            if (ok) setEditingLine(null);
            return ok;
          }}
        />
      )}
      {editingTravel && (
        <TravelClaimModal
          claim={editingTravel}
          onCancel={() => setEditingTravel(null)}
          onSave={async (patch) => {
            const ok = await action(
              updateTravelClaim(run.billingRunId, editingTravel.travelClaimId, patch),
            );
            if (ok) setEditingTravel(null);
            return ok;
          }}
        />
      )}
      {acknowledging && (
        <NoteModal
          title={`Acknowledge warning · ${acknowledging.label}`}
          actionLabel="Save acknowledgement"
          onCancel={() => setAcknowledging(null)}
          onSave={async (note) => {
            const ok = await action(
              acknowledgeBillingWarning(run.billingRunId, acknowledging.code, note),
            );
            if (ok) setAcknowledging(null);
            return ok;
          }}
        />
      )}
    </div>
  );
}

function TravelClaimModal({
  claim,
  onCancel,
  onSave,
}: {
  claim: TravelClaim;
  onCancel: () => void;
  onSave: (patch: TravelClaimPatch) => Promise<boolean>;
}) {
  const [form, setForm] = useState<TravelClaimPatch>({
    baseLabel: claim.baseLabel,
    baseAddress: claim.baseAddress,
    routeKm: claim.routeKm,
    accommodationNights: claim.accommodationNights,
    actualAccommodationCost: claim.actualAccommodationCost,
    mealQuantities: claim.mealQuantities,
    status: claim.status,
    reviewNote: claim.reviewNote,
  });
  const [saving, setSaving] = useState(false);
  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <div className="section-heading">
          <div>
            <h3>Review travel claim</h3>
            <p>
              {claim.date} · {claim.jobIds.length} job{claim.jobIds.length === 1 ? '' : 's'}
            </p>
          </div>
          <button onClick={onCancel}>Close</button>
        </div>
        <div className="form-grid">
          <label>
            Base label
            <input
              value={form.baseLabel}
              onChange={(e) => setForm({ ...form, baseLabel: e.target.value })}
            />
          </label>
          <label>
            Base address
            <input
              value={form.baseAddress}
              onChange={(e) => setForm({ ...form, baseAddress: e.target.value })}
            />
          </label>
          <label>
            Supplier route kilometres
            <input
              type="number"
              min="0"
              step="0.1"
              value={form.routeKm ?? ''}
              onChange={(e) =>
                setForm({ ...form, routeKm: e.target.value === '' ? null : Number(e.target.value) })
              }
            />
          </label>
          <label>
            Accommodation nights
            <input
              type="number"
              min="0"
              step="1"
              value={form.accommodationNights}
              onChange={(e) => setForm({ ...form, accommodationNights: Number(e.target.value) })}
            />
          </label>
          <label>
            Actual accommodation cost
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.actualAccommodationCost ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  actualAccommodationCost: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          </label>
          <label>
            Status
            <select
              value={form.status}
              onChange={(e) =>
                setForm({ ...form, status: e.target.value as TravelClaimPatch['status'] })
              }
            >
              <option value="needs-review">Needs review</option>
              <option value="ready">Ready</option>
              <option value="approved">Approved</option>
              <option value="excluded">Excluded</option>
            </select>
          </label>
        </div>
        <fieldset>
          <legend>Meal quantities</legend>
          <div className="form-grid compact">
            {(['breakfast', 'lunch', 'dinner', 'fullDay'] as const).map((meal) => (
              <label key={meal}>
                {meal}
                <input
                  type="number"
                  min="0"
                  value={form.mealQuantities[meal]}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      mealQuantities: { ...form.mealQuantities, [meal]: Number(e.target.value) },
                    })
                  }
                />
              </label>
            ))}
          </div>
        </fieldset>
        <label>
          Reason for review or override
          <textarea
            rows={3}
            required
            value={form.reviewNote}
            onChange={(e) => setForm({ ...form, reviewNote: e.target.value })}
          />
        </label>
        <p>
          <strong>Stops:</strong> {claim.stopAddresses.join(' → ')}
        </p>
        <div className="form-actions">
          <button onClick={onCancel}>Cancel</button>
          <button
            className="primary"
            disabled={saving || !form.reviewNote.trim()}
            onClick={() => {
              setSaving(true);
              void onSave(form).finally(() => setSaving(false));
            }}
          >
            {saving ? 'Saving…' : 'Save review'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BillingFactsModal({
  job,
  availableExtras,
  onCancel,
  onSave,
}: {
  job: InspectionJob;
  availableExtras: Array<{ code: string; label: string }>;
  onCancel: () => void;
  onSave: (facts: BillingJobFactsPatch) => Promise<boolean>;
}) {
  const [form, setForm] = useState({
    systemSizeKw: String(job.systemSizeKw ?? ''),
    travelKm: String(job.travelKm ?? ''),
    accommodationNights: String(job.accommodationNights ?? ''),
    serviceM8JobNumber: job.serviceM8JobNumber ?? '',
    revisit: job.revisit ?? false,
    billingNotes: job.billingNotes ?? '',
    billingExtras: job.billingExtras ?? [],
    resolutionNote: '',
  });
  const [busy, setBusy] = useState(false);
  const optionalNumber = (value: string) => (value === '' ? undefined : Number(value));
  async function submit() {
    setBusy(true);
    try {
      await onSave({
        ...(optionalNumber(form.systemSizeKw) !== undefined
          ? { systemSizeKw: optionalNumber(form.systemSizeKw) }
          : {}),
        ...(optionalNumber(form.travelKm) !== undefined
          ? { travelKm: optionalNumber(form.travelKm) }
          : {}),
        ...(optionalNumber(form.accommodationNights) !== undefined
          ? { accommodationNights: optionalNumber(form.accommodationNights) }
          : {}),
        ...(form.serviceM8JobNumber.trim()
          ? { serviceM8JobNumber: form.serviceM8JobNumber.trim() }
          : {}),
        revisit: form.revisit,
        billingNotes: form.billingNotes,
        billingExtras: form.billingExtras,
        resolutionNote: form.resolutionNote,
      });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="modal" role="presentation">
      <div
        className="modal__card billing-facts-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-facts-title"
      >
        <h3 id="billing-facts-title">Resolve billing QA · {job.jobId}</h3>
        <p className="muted">
          Correct the source Job facts. Saving reprices the run and re-runs every QA check.
        </p>
        <div className="form-grid">
          <label>
            System size (kW)
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.systemSizeKw}
              onChange={(e) => setForm({ ...form, systemSizeKw: e.target.value })}
            />
          </label>
          <label>
            Travel (km)
            <input
              type="number"
              min="0"
              step="0.1"
              value={form.travelKm}
              onChange={(e) => setForm({ ...form, travelKm: e.target.value })}
            />
          </label>
          <label>
            Accommodation nights
            <input
              type="number"
              min="0"
              step="1"
              value={form.accommodationNights}
              onChange={(e) => setForm({ ...form, accommodationNights: e.target.value })}
            />
          </label>
          <label>
            ServiceM8 job #
            <input
              value={form.serviceM8JobNumber}
              onChange={(e) => setForm({ ...form, serviceM8JobNumber: e.target.value })}
            />
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.revisit}
              onChange={(e) => setForm({ ...form, revisit: e.target.checked })}
            />{' '}
            Revisit
          </label>
        </div>
        <EditableTable
          title="Extras and allowances"
          columns={['Allowance', 'Quantity', '']}
          onAdd={() =>
            setForm({
              ...form,
              billingExtras: [
                ...form.billingExtras,
                { code: availableExtras[0]?.code ?? '', quantity: 1 },
              ],
            })
          }
        >
          {form.billingExtras.map((row, index) => (
            <div className="editable-grid editable-grid--job-extras" key={index}>
              <select
                aria-label={`Job extra ${index + 1} allowance`}
                value={row.code}
                onChange={(e) =>
                  setForm({
                    ...form,
                    billingExtras: form.billingExtras.map((item, i) =>
                      i === index ? { ...item, code: e.target.value } : item,
                    ),
                  })
                }
              >
                <option value="">Select allowance…</option>
                {availableExtras.map((extra) => (
                  <option key={extra.code} value={extra.code}>
                    {extra.label}
                  </option>
                ))}
              </select>
              <input
                aria-label={`Job extra ${index + 1} quantity`}
                type="number"
                min="0.01"
                step="0.01"
                value={row.quantity}
                onChange={(e) =>
                  setForm({
                    ...form,
                    billingExtras: form.billingExtras.map((item, i) =>
                      i === index ? { ...item, quantity: numberValue(e.target.value) } : item,
                    ),
                  })
                }
              />
              <button
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    billingExtras: form.billingExtras.filter((_, i) => i !== index),
                  })
                }
              >
                Remove
              </button>
            </div>
          ))}
        </EditableTable>
        <label>
          Billing notes
          <textarea
            rows={3}
            value={form.billingNotes}
            onChange={(e) => setForm({ ...form, billingNotes: e.target.value })}
          />
        </label>
        <label>
          Resolution note (required)
          <textarea
            rows={2}
            value={form.resolutionNote}
            onChange={(e) => setForm({ ...form, resolutionNote: e.target.value })}
          />
        </label>
        <div className="modal__actions">
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => void submit()}
            disabled={busy || !form.resolutionNote.trim()}
          >
            {busy ? 'Saving…' : 'Save, reprice & re-run QA'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BillingLineRow({
  run,
  line,
  onEdit,
}: {
  run: BillingRun;
  line: BillingLine;
  onEdit: (line: BillingLine) => void;
}) {
  return (
    <div className="billing-line">
      <div>
        <strong>{line.description}</strong>
        <small>
          {line.completedAt} · SM8 #{line.serviceM8JobNumber ?? 'missing'}
        </small>
        <details>
          <summary>Why this price?</summary>
          {line.pricingReason.map((reason) => (
            <div key={reason}>{reason}</div>
          ))}
        </details>
      </div>
      <span>{money(line.inspectionFee)}</span>
      <span>{money(line.travel + line.accommodation)}</span>
      <span>{money(line.extras)}</span>
      <strong>{money(line.total)}</strong>
      {run.status === 'draft' ? (
        <button type="button" onClick={() => onEdit(line)}>
          Edit
        </button>
      ) : (
        <span />
      )}
    </div>
  );
}

function BillingLineModal({
  line,
  onCancel,
  onSave,
}: {
  line: BillingLine;
  onCancel: () => void;
  onSave: (line: BillingLine) => Promise<boolean>;
}) {
  const [form, setForm] = useState({
    inspectionFee: line.inspectionFee,
    travel: line.travel,
    accommodation: line.accommodation,
    extras: line.extras,
    editNote: line.editNote,
  });
  const [busy, setBusy] = useState(false);
  const total = form.inspectionFee + form.travel + form.accommodation + form.extras;
  return (
    <div className="modal">
      <div
        className="modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="line-edit-title"
      >
        <h3 id="line-edit-title">Edit invoice line</h3>
        <p>
          <strong>{line.description}</strong>
        </p>
        <div className="form-grid">
          <label>
            Inspection fee
            <input
              type="number"
              step="0.01"
              value={form.inspectionFee}
              onChange={(e) => setForm({ ...form, inspectionFee: numberValue(e.target.value) })}
            />
          </label>
          <label>
            Travel
            <input
              type="number"
              step="0.01"
              value={form.travel}
              onChange={(e) => setForm({ ...form, travel: numberValue(e.target.value) })}
            />
          </label>
          <label>
            Accommodation
            <input
              type="number"
              step="0.01"
              value={form.accommodation}
              onChange={(e) => setForm({ ...form, accommodation: numberValue(e.target.value) })}
            />
          </label>
          <label>
            Extras
            <input
              type="number"
              step="0.01"
              value={form.extras}
              onChange={(e) => setForm({ ...form, extras: numberValue(e.target.value) })}
            />
          </label>
          <label>
            Total
            <input readOnly value={total.toFixed(2)} />
          </label>
        </div>
        <label>
          Reason for change (required)
          <textarea
            rows={3}
            value={form.editNote}
            onChange={(e) => setForm({ ...form, editNote: e.target.value })}
          />
        </label>
        <div className="modal__actions">
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy || !form.editNote.trim()}
            onClick={() => {
              setBusy(true);
              void onSave({ ...line, ...form, total, manuallyEdited: true }).finally(() =>
                setBusy(false),
              );
            }}
          >
            {busy ? 'Saving…' : 'Save line'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteModal({
  title,
  actionLabel,
  onCancel,
  onSave,
}: {
  title: string;
  actionLabel: string;
  onCancel: () => void;
  onSave: (note: string) => Promise<boolean>;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="modal">
      <div
        className="modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="note-modal-title"
      >
        <h3 id="note-modal-title">{title}</h3>
        <label>
          Note (required)
          <textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="modal__actions">
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy || !note.trim()}
            onClick={() => {
              setBusy(true);
              void onSave(note.trim()).finally(() => setBusy(false));
            }}
          >
            {busy ? 'Saving…' : actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Ledger({
  rows,
  total,
  clients,
}: {
  rows: UninvoicedJob[];
  total: number;
  clients: Client[];
}) {
  // Aggregates of the rows below, nothing more — the ageing figures mirror the
  // `age-risk` highlight the table already applies at 30 days.
  const ageing = rows.filter((row) => row.ageDays > 30).length;
  const oldest = rows.reduce((max, row) => Math.max(max, row.ageDays), 0);
  return (
    <>
      <section className="overview-kpis overview-kpis--four" aria-label="Uninvoiced totals">
        <Kpi label="Uninvoiced Jobs" value={rows.length} unit="Jobs" tone="blue" icon="clipboard" />
        <Kpi label="Estimated Value" value={total} tone="green" icon="dollar" formatValue={money} />
        <Kpi
          label="Over 30 Days"
          value={ageing}
          unit="Jobs"
          tone={ageing ? 'amber' : 'green'}
          icon="alert"
          percent={percentage(ageing, rows.length)}
          percentCaption="of uninvoiced"
          footnote={ageing ? undefined : { text: 'Nothing ageing', tone: 'good' }}
        />
        <Kpi
          label="Oldest"
          value={oldest}
          unit="Days"
          tone={oldest > 30 ? 'red' : 'neutral'}
          icon="clock"
        />
      </section>

      <section className="overview-band">
        <div className="band-heading">
          <h3>Completed Jobs not on any billing run</h3>
          <span>
            {rows.length} job{rows.length === 1 ? '' : 's'} · {money(total)}
          </span>
        </div>
        {rows.length === 0 ? (
          <p className="loading">Every completed Job is on a billing run.</p>
        ) : (
          <div role="table">
            <div className="overview-jobs-row ledger-row ledger-row--header" role="row">
              <div>Job</div>
              <div>Client</div>
              <div>Completed</div>
              <div>Age</div>
              <div>Address</div>
              <div>Estimate</div>
            </div>
            {rows.map((row) => (
              <div className="overview-jobs-row ledger-row" role="row" key={row.jobId}>
                <div className="mono">{row.jobId}</div>
                <div>
                  {clients.find((c) => c.clientId === row.clientId)?.shortName ?? row.clientId}
                </div>
                <div>{row.completedAt}</div>
                <div className={row.ageDays > 30 ? 'age-risk' : ''}>{row.ageDays} days</div>
                <div>{row.address}</div>
                <div>
                  <strong>{money(row.estimatedValue)}</strong>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
